import { Snackbar, Alert } from "@mui/material";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DynamicForm from "../../common/DynamicForm/DynamicForm";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  EditAddTask,
  getAddTaskFormFieldsJson,
  PostAddTask,
} from "./AddTaskActions";
import { RootState } from "../../store/store";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
  IGetOptionsResponse,
} from "../../models/user/DynamicForm/DynamicForm";
import { getCookie } from "../../utils/Utility";
import { Api } from "../..";
import { API_GET_LOOKUP_VALUES } from "../../Apis/ApiEndPoints";
import { CustomAction } from "../../store/customAction";
import { IPostAddTaskResponseData } from "../../models/agent/Tasks/addTask";
import dayjs from "dayjs";
import CustomDrawer from "../../common/CustomDrawer/CustomDrawer";
import { IAgentTask } from "../../models/agent/policiesListing";
import { useParams } from "react-router-dom";
type taskFormProps = {
  open: boolean;
  handleClose: () => void;
  isUpdate?: boolean;
  taskName?: string;
  taskData?: IAgentTask | {};
};

const AddTaskForm: React.FC<taskFormProps> = ({
  open,
  handleClose,
  isUpdate = false,
  taskData,
  taskName,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { t } = useTranslation();

  const [isPolicyDetailPage, setisPolicyDetailsPage] = useState(false);
  const { addTaskFormFieldsJson } = useAppSelector(
    (state: RootState) => state.addTask
  );
  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);
  const dispatch = useAppDispatch();
  const params = useParams();
  const fullPath = params["*"];
  const pathSegments = fullPath ? fullPath.split("/") : [];
  const policyId = pathSegments[1] || null;

  const providerId = getCookie("id");

  const { policyDetailsHeader } = useAppSelector(
    (store: RootState) => store.policyDetails
  );

  const { data } = useAppSelector((state) => state.agentTasks);

  useEffect(() => {
    dispatch(getAddTaskFormFieldsJson());
    const isPolicy = window.location.pathname.includes("policyDetails");
    setisPolicyDetailsPage(isPolicy);
  }, []);

  useEffect(() => {
    if (addTaskFormFieldsJson) {
      setFormFieldsJson(addTaskFormFieldsJson);
    }
  }, [addTaskFormFieldsJson]);

  useEffect(() => {
    if (addTaskFormFieldsJson) {
      const updatedFormFieldsJson = {
        ...addTaskFormFieldsJson,
        sections:
          addTaskFormFieldsJson.sections?.map((section) => ({
            ...section,
            fields: section?.fields.map((field) => {
              const updatedField = { ...field };
              if (isPolicyDetailPage) {
                if (updatedField.name === "customerName") {
                  updatedField.value = policyDetailsHeader?.insuredName;
                }
                if (updatedField.name === "policyNumber") {
                  updatedField.value = policyDetailsHeader?.carrierPolicyNumber;
                }
              }
              if (updatedField.name === "priority") {
                updatedField.value = "medium";
              }
              if (isUpdate) {
                if (updatedField.name === "customerName") {
                  updatedField.value = (
                    taskData as IPostAddTaskResponseData
                  )?.customerName;
                }
                if (updatedField.name === "policyNumber") {
                  updatedField.value = (
                    taskData as IPostAddTaskResponseData
                  )?.policyNumber;
                }
                if (updatedField.name === "priority") {
                  updatedField.value = (
                    taskData as IPostAddTaskResponseData
                  )?.priority;
                }
                if (updatedField.name === "description") {
                  updatedField.value = (
                    taskData as IPostAddTaskResponseData
                  )?.description;
                }
                if (updatedField.name === "taskName") {
                  updatedField.value = (
                    taskData as IPostAddTaskResponseData
                  )?.taskName;
                }
              }
              return updatedField;
            }),
          })) || [],
      };
      setFormFieldsJson(updatedFormFieldsJson);
    }
  }, [addTaskFormFieldsJson, taskData]);

  const asyncOptionsFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      getPriorityValues: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getPriorityValues"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
    }),
    []
  );
  const id = (taskData as IPostAddTaskResponseData)?.id;

  const handleSubmit = useCallback(
    async (formValues: { [key: string]: any }) => {
      const requestBody = {
        taskName: formValues.taskName,
        customerName: formValues.customerName,
        policyNumber: formValues.policyNumber,
        description: formValues.description,
        dueDate: formValues.dueDate?.format("YYYY-MM-DD") || "",
        dueTime: formValues.time
          ? formValues.time.format("HH:mm:ss")
          : "00:00:00",
        providerId: providerId,
        policyId,
        priority: formValues.priority,
        status: "Open",
      };

      if (isUpdate) {
        const editRequestBody = { ...requestBody, id };
        try {
          await dispatch(EditAddTask(editRequestBody));
          setSnackbarMessage("Task Updated Successfully");
          handleClose();
        } catch (error) {
          setSnackbarMessage("Failed to Update Task");
        }
      } else {
        try {
          await dispatch(PostAddTask(requestBody));
          setSnackbarMessage("Task Saved Successfully");
          handleClose();
        } catch (error) {
          setSnackbarMessage("Failed to Save Task");
        }
      }
      setSnackbarOpen(true);
    },
    [id]
  );

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <CustomDrawer
        open={open}
        onClose={handleClose}
        title={isUpdate ? `${taskName}` : t("Add Task")}
        content={
          formFieldsJson ? (
            <DynamicForm
              formFieldsJson={formFieldsJson}
              onFormSubmit={handleSubmit}
              asyncOptionsFunctions={asyncOptionsFunctions}
              isUpdate={isUpdate}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
};

export default AddTaskForm;
